import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';

import { TotalAmountRange } from '../../../models/total-amount-range.enum';
import { newFormControl } from '../../../tools/new-form-control.tools';
import { User } from '../../authentication/models/v1-api.interface';
import { AuthenticationService } from '../../authentication/services/authentication.service';
import { ProfileService } from '../../profile/services/profile.service';
import { Day } from '../../shared/models/day.type';
import { DateService } from '../../shared/services/date.service';
import { SharedService } from '../../shared/services/shared.service';
import { TrackingService } from '../../shared/services/tracking.service';
import { dayRequiredValidator } from '../../shared/validators/day-required.validator';
import { over18YearsOldValidator } from '../../shared/validators/over-18-years-old.validator';
import { shippingAddressValidator } from '../../shared/validators/shipping-address.validator';
import { shippingAddressRequiredIfUseValidator } from '../../shared/validators/shipping-address-required-if-use.validator';
import {
  InviteSignupProcessStepEnum,
  inviteSignupProcessSteps,
} from '../models/invite-signup-process-step.enum';

@Injectable()
export class InviteSignupProcessService {
  public identityFormControls: Partial<
    Record<keyof User | string, FormControl>
  > = {
    firstName: newFormControl('', [
      Validators.required,
      Validators.minLength(2),
    ]),
    lastName: newFormControl('', [
      Validators.required,
      Validators.minLength(2),
    ]),
    line1Address: newFormControl('', [
      Validators.required,
      shippingAddressValidator(),
    ]),
    city: newFormControl('', [Validators.required]),
    state: newFormControl('', [Validators.required]),
    zip: newFormControl('', [Validators.required]),
    sendToHomeAddress: newFormControl(true, []), // useShippingAddress = !sendToHomeAddress
    shippingZip: newFormControl('', [shippingAddressRequiredIfUseValidator()]),
    shippingCity: newFormControl('', [shippingAddressRequiredIfUseValidator()]),
    shippingState: newFormControl('', [
      shippingAddressRequiredIfUseValidator(),
    ]),
    shippingLine1Address: newFormControl('', [
      shippingAddressRequiredIfUseValidator(),
      shippingAddressValidator(true),
    ]),
  };

  public identityForm = this.getFilteredIdentityForm();

  public addressForm = this.formBuilder.group({
    line1Address: newFormControl('', [Validators.required]),
    city: newFormControl('', [Validators.required]),
    state: newFormControl('', [Validators.required]),
    zip: newFormControl('', [Validators.required, Validators.maxLength(5)]),
  });

  public phoneForm = this.formBuilder.group({
    phoneNumber: newFormControl('', [
      Validators.required,
      Validators.minLength(14),
    ]),
  });

  public smsForm = this.formBuilder.group({
    confirmationCode: newFormControl('', [
      Validators.required,
      Validators.minLength(5),
      Validators.maxLength(5),
    ]),
  });

  public detailsFormControls: Partial<
    Record<keyof User | string, FormControl>
  > = {
    dateOfBirth: newFormControl(0 as Day, [
      dayRequiredValidator,
      over18YearsOldValidator(this.dateService),
    ]),
    ssn: newFormControl('', [Validators.required, Validators.minLength(4)]),
    mls: new FormControl('', [Validators.required, Validators.minLength(2)]),
  };

  public detailsForm = this.getFilteredDetailsForm();

  public dealQaForm: FormGroup = this.formBuilder.group({
    completedTransactions: newFormControl(0 as number | undefined, [
      Validators.required,
      Validators.min(0),
      Validators.max(999999),
      Validators.pattern('^[0-9]*'),
    ]),
    last12MonthsTotalAmountRange: new FormControl<TotalAmountRange | undefined>(
      undefined,
      {
        validators: [Validators.required],
      },
    ),
  });

  public step = new BehaviorSubject(InviteSignupProcessStepEnum.Identity);
  public close = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authenticationService: AuthenticationService,
    private toastrService: ToastrService,
    private sharedService: SharedService,
    private dateService: DateService,
    private trackingService: TrackingService,
    private profileService: ProfileService,
  ) {
    this.identityForm.controls.sendToHomeAddress?.valueChanges.subscribe(() => {
      [
        'line1Address',
        'shippingZip',
        'shippingCity',
        'shippingState',
        'shippingLine1Address',
      ].forEach((controlName) =>
        this.identityForm.get(controlName)?.updateValueAndValidity(),
      );
      this.identityForm.updateValueAndValidity();
    });
  }

  public nextStep(): void {
    this.step.next(inviteSignupProcessSteps[this.step.getValue() + 1]);
  }

  public updateIdentity(): Observable<User> {
    const user = this.authenticationService.getProfileState();
    const raw = this.identityForm.getRawValue();
    if (raw.sendToHomeAddress) {
      raw.shippingZip = raw.zip || user.zip;
      raw.shippingCity = raw.city || user.city;
      raw.shippingState = raw.state || user.state;
      raw.shippingLine1Address = raw.line1Address || user.line1Address;
    }
    if (typeof raw.sendToHomeAddress === 'boolean') {
      raw.useShippingAddress = !raw.sendToHomeAddress;
    }

    return this.profileService.updateProfile(raw, false);
  }

  public updateAddress(): Observable<User> {
    const raw = this.addressForm.getRawValue();
    return this.profileService.updateProfile(raw, false);
  }

  public updateDetails(): Observable<User> {
    return this.profileService.updateProfile(
      this.detailsForm.getRawValue(),
      false,
    );
  }

  public updateDealQa(): Observable<User> {
    return this.profileService.updateProfile(
      this.dealQaForm.getRawValue(),
      false,
    );
  }

  public async initiatePreVerifyPhone(): Promise<void> {
    await this.authenticationService
      .initiatePreVerifyPhone(this.getPhoneNumber())
      .toPromise();
  }

  public async sendConfirmationCode(): Promise<void> {
    const results = await firstValueFrom(
      this.authenticationService.confirmPreVerifyPhone(
        this.getPhoneNumber(),
        this.smsForm.controls.confirmationCode.value,
      ),
    );

    if (results.success) {
      await this.authenticationService.getProfile().toPromise();
    }

    this.trackingService.track('conversion', {
      send_to: 'AW-937185556/AyBbCI3T2ZEDEJSi8b4D',
    });
  }

  private getFilteredIdentityForm(): FormGroup<
    Record<keyof User | string, FormControl>
  > {
    const user = this.authenticationService.getProfileState();
    const controls: Partial<Record<keyof User | string, FormControl>> = {};
    const fields: Array<keyof User> = [
      'firstName',
      'lastName',
      'line1Address',
      'city',
      'state',
      'zip',
    ];
    for (const field of fields) {
      if (user[field]) {
        continue;
      }

      controls[field] = this.identityFormControls[field];
    }

    if (!user.shippingLine1Address) {
      const shippingFields = [
        'sendToHomeAddress',
        'shippingZip',
        'shippingCity',
        'shippingState',
        'shippingLine1Address',
      ];
      shippingFields.forEach(
        (field) => (controls[field] = this.identityFormControls[field]),
      );
    }

    return this.formBuilder.group(controls);
  }

  private getFilteredDetailsForm(): FormGroup<
    Record<keyof User | string, FormControl>
  > {
    const user = this.authenticationService.getProfileState();
    const controls: Partial<Record<keyof User, FormControl>> = {};
    for (const field in this.detailsFormControls) {
      if (user[field as keyof User]) {
        continue;
      }

      controls[field as keyof User] = this.detailsFormControls[field];
    }

    return this.formBuilder.group(controls);
  }

  private getPhoneNumber(): string {
    return '1' + this.phoneForm.controls.phoneNumber.value.replace(/\D+/g, '');
  }
}
